import { useState } from 'react';
import { AlertBanner, Badge, Button, Dialog, EmptyState, Input, Toast } from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import { ApiError, endpoints, type FindingSummary } from '../state/api.js';
import { useSession } from '../state/session.js';
import { useResource } from '../lib/useResource.js';
import { Screen } from '../components/Screen.js';
import { formatDate } from '../lib/format.js';

/**
 * ANTREAN VERIFIKASI — rute `/verification-queue` · M02 · izin LDR MGR.
 *
 * ACUAN RANCANGANNYA ADALAH LAYAR PONSEL "Verification Queue" (peta §5.10). Sama seperti
 * layar Lapor Temuan, ia TAMBAHAN terhadap ke-35 layar peta dan dicatat di
 * `LAYAR-CONSOLE.md` — dan alasannya sama: alur 2 §6 berakhir di sini, dan tanpa layar ini
 * alur itu berhenti pada langkah terakhirnya.
 *
 * DUA AKSI, DAN SATU DI ANTARANYA MENUNTUT ALASAN
 * ----------------------------------------------
 * `CONFIRM_FIX` memfinalkan poin pelapor DAN penutup — ia satu-satunya jalan poin berpindah
 * dari provisional ke final (peta §6 alur 2). `SEND_BACK` menuntut alasan yang akan DIBACA
 * orang yang mengerjakannya, dan guard `reason_required` menolak tanpanya.
 *
 * Yang TIDAK dilakukan layar ini: memutuskan sendiri apakah tombolnya boleh muncul.
 * `available_transitions` datang dari server per butir, beserta `blocked_by`-nya — termasuk
 * `SELF_VERIFICATION_NOT_ALLOWED`, yang berarti leader yang menutup temuannya sendiri tidak
 * dapat memverifikasinya. Sebuah daftar tombol yang disusun klien akan menawarkan aksi itu
 * lalu gagal setelah ditekan.
 */
export function VerificationQueue({ onOpen }: { onOpen: (code: string) => void }) {
  const { t, locale, translator } = useSession();
  const queue = useResource(async () => (await endpoints.verificationQueue()).items, []);
  const [sendBack, setSendBack] = useState<FindingSummary | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'warning'; text: string } | null>(null);

  async function jalankan(code: string, transition: 'CONFIRM_FIX' | 'SEND_BACK', alasan?: string) {
    setBusy(code);
    setProblem(null);
    try {
      await endpoints.transition(code, {
        transition,
        reason: alasan ?? null,
        input: transition === 'SEND_BACK' ? { reason_code: 'EVIDENCE_INSUFFICIENT' } : {},
      });
      setToast({
        kind: transition === 'CONFIRM_FIX' ? 'success' : 'warning',
        text:
          transition === 'CONFIRM_FIX'
            ? t('verify.confirmed', { code })
            : t('verify.sentBack', { code }),
      });
      setSendBack(null);
      setReason('');
      await queue.reload();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Screen
      title={t('verify.title')}
      subtitle={t('verify.subtitle')}
      resource={queue}
      moduleCode="M02"
    >
      {(items) => (
        <>
          {problem ? (
            <AlertBanner
              kind="danger"
              signalWord={t('signal.danger')}
              title={renderProblem(translator, problem.problem)}
            />
          ) : null}

          {items.length === 0 ? (
            /*
              Antrean kosong di sini adalah KABAR BAIK, bukan "belum ada data": ia berarti
              tidak ada perbaikan yang menunggu keputusan siapa pun. Kalimatnya karena itu
              berbeda dari kosong murni pada Daftar Temuan.
            */
            <EmptyState title={t('verify.empty.title')} body={t('verify.empty.body')} />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('findings.column.code')}</th>
                  <th scope="col">{t('findings.column.title')}</th>
                  <th scope="col">{t('findings.column.area')}</th>
                  <th scope="col">{t('verify.column.closedBy')}</th>
                  <th scope="col">{t('findings.column.photos')}</th>
                  <th scope="col">{t('verify.column.decide')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <button
                        className="linkish"
                        type="button"
                        onClick={() => item.code && onOpen(item.code)}
                      >
                        {item.code ?? '—'}
                      </button>
                    </td>
                    <td>
                      {item.title}
                      {item.is_safety ? (
                        <>
                          {' '}
                          <Badge tone="danger">{t('category.SAFETY')}</Badge>
                        </>
                      ) : null}
                    </td>
                    <td>{item.location?.code ?? '—'}</td>
                    <td>{item.owner?.full_name ?? t('findings.unassigned')}</td>
                    <td>{item.photo_count}</td>
                    <td className="page__actions">
                      {/*
                        Foto sebelum/sesudah BERDAMPINGAN ada di layar detail, bukan di
                        baris tabel ini — dan tombol "Konfirmasi" di sini membawa ke sana
                        lebih dulu bila fotonya belum pernah dilihat. Peta §5.10 menuntut
                        perbandingan foto sebagai bagian verifikasi; memverifikasi dari
                        baris tabel adalah tanda tangan di atas sesuatu yang tidak dilihat.
                      */}
                      <Button
                        variant="secondary"
                        onClick={() => item.code && onOpen(item.code)}
                      >
                        {t('verify.action.comparePhotos')}
                      </Button>
                      <Button
                        variant="primary"
                        busy={busy === item.code}
                        onClick={() => item.code && void jalankan(item.code, 'CONFIRM_FIX')}
                      >
                        {t('verify.action.confirm')}
                      </Button>
                      <Button variant="secondary" onClick={() => setSendBack(item)}>
                        {t('verify.action.sendBack')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <Dialog
            open={sendBack !== null}
            kind="confirm"
            title={t('verify.sendBack.title')}
            description={t('verify.sendBack.describe', { code: sendBack?.code ?? '' })}
            confirmLabel={t('verify.action.sendBack')}
            cancelLabel={t('app.cancel')}
            busy={busy !== null}
            confirmDisabled={reason.trim().length < 3}
            onConfirm={() => {
              if (sendBack?.code) void jalankan(sendBack.code, 'SEND_BACK', reason.trim());
            }}
            onCancel={() => {
              setSendBack(null);
              setReason('');
            }}
          >
            <Input
              label={t('finding.reason')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              hint={t('verify.sendBack.reasonHint')}
              error={reason.trim().length < 3 ? t('error.REASON_REQUIRED') : null}
            />
          </Dialog>

          {toast ? (
            <div className="assist-toast-stack">
              <Toast
                kind={toast.kind}
                signalWord={toast.kind === 'success' ? t('signal.success') : t('signal.warning')}
                title={toast.text}
                onDismiss={() => setToast(null)}
                closeLabel={t('app.dismiss')}
              />
            </div>
          ) : null}

          <p className="card__muted">{t('verify.footnote', { today: formatDate(new Date().toISOString(), locale) })}</p>
        </>
      )}
    </Screen>
  );
}
